import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { PublicPayService } from '../../services/paymentLink.service';
import { PayPage, PayResult, PaymentProvider } from '../../interfaces/sales/paymentLink.interface';
import { formatMoney } from '../../utils/format';

/** The pay URL carries the token: never leak it as a Referer to anything the page links or loads. */
const useNoReferrer = () => {
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'referrer';
    m.content = 'no-referrer';
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);
};

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useNoReferrer();
  return (
  <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', p: 2, pt: 8, bgcolor: 'background.default' }}>
    <Paper sx={{ p: 3, width: '100%', maxWidth: 440 }}>{children}</Paper>
  </Box>
  );
};

/** Public, unauthenticated pay page: /pay/:token. Shows only the invoice (never institution settings). */
export const PublicPayPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [page, setPage] = useState<PayPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    PublicPayService.details(token as string)
      .then(p => { if (live) setPage(p); })
      .catch(() => { if (live) setError('This payment link is not valid.'); });
    return () => { live = false; };
  }, [token]);

  const pay = async (provider: PaymentProvider) => {
    try {
      setBusy(true);
      const r = await PublicPayService.start(token as string, provider);
      window.location.assign(r.redirectUrl);
    } catch {
      setError('We could not start the payment. Please try again or contact the seller.');
      setBusy(false);
    }
  };

  if (error) return <Shell><Alert severity="error">{error}</Alert></Shell>;
  if (!page) return <Shell><CircularProgress /></Shell>;
  const payable = page.status === 'OPEN';
  return (
    <Shell>
      {page.sandbox && <Alert severity="warning" sx={{ mb: 2 }}>SANDBOX MODE: no real money will move.</Alert>}
      <Typography variant="overline">{page.institutionName}</Typography>
      <Typography variant="h5" gutterBottom>Invoice {page.invoiceNumber}</Typography>
      <Typography variant="h4" sx={{ my: 2 }}>{formatMoney(page.amountDue)} {page.currency}</Typography>
      {!payable && (
        <Alert severity={page.status === 'PAID' ? 'success' : 'info'}>
          {page.status === 'PAID' ? 'This invoice has been paid. Thank you.' : 'This payment link is no longer active. Please contact the seller.'}
        </Alert>
      )}
      {payable && page.providers.length === 0 && <Alert severity="info">Online payment is not available right now.</Alert>}
      {payable && page.providers.map(p => (
        <Button key={p.provider} fullWidth variant="contained" sx={{ mb: 1 }} disabled={busy} onClick={() => pay(p.provider)}>
          Pay with {p.label}
        </Button>
      ))}
    </Shell>
  );
};

/** Where the gateway sends the payer back: /pay/result/:ref (an opaque reference, never the token). */
export const PublicPayResultPage: React.FC = () => {
  const { ref } = useParams<{ ref: string }>();
  const [res, setRes] = useState<PayResult | null>(null);
  useEffect(() => {
    let live = true;
    PublicPayService.result(ref as string)
      .then(r => { if (live) setRes(r); })
      .catch(() => { if (live) setRes({ result: 'unknown', sandbox: false }); });
    return () => { live = false; };
  }, [ref]);

  if (!res) return <Shell><CircularProgress /></Shell>;
  const msg: Record<string, [('success' | 'info' | 'warning' | 'error'), string]> = {
    PAID: ['success', 'Payment received. Thank you.'],
    PROCESSING: ['info', 'Your payment is being confirmed. Please do not pay again; this can take a few minutes.'],
    FAILED: ['error', 'The payment was not completed. You can use your link to try again.'],
    unknown: ['info', 'We could not find this payment.'],
  };
  const [severity, text] = msg[res.result] ?? msg.unknown;
  return (
    <Shell>
      {res.sandbox && <Alert severity="warning" sx={{ mb: 2 }}>SANDBOX MODE: no real money moved.</Alert>}
      {res.invoiceNumber && <Typography variant="h6" gutterBottom>Invoice {res.invoiceNumber}</Typography>}
      <Alert severity={severity}>{text}</Alert>
    </Shell>
  );
};
